import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Boxes, Megaphone, PackageOpen, Phone } from "lucide-react";
import {
  STORE_BANNER_BGS,
  STORE_CURRENCY,
  storePath,
  type StoreItem,
} from "@nh/shared";
import { Alert, Button } from "@/shared/ui/core";
import { EmptyState } from "@/shared/ui/ui";
import { ProductCard } from "@/features/shop/components/ProductCard";
import type { ResolvedPrice } from "@/features/shop/shop.catalog";
import { usePublicStore } from "./store.api";
import { StoreHero } from "./StoreHero";
import { StoreLayout } from "./StoreLayout";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La vitrina de una tienda personalizada ("Annieshop") — rediseñada con
 * el lenguaje de la tienda de la casa (Anahí, 27-ago): héroe con
 * resplandores, chips de categoría y la rejilla de tarjetas grandes.
 * La diferencia es QUIÉN manda en el color: aquí todo el acento sale de
 * `branding.brandColor`, que el franquiciatario elige en Mi tienda — su
 * tienda debe sentirse SUYA, no una copia del catálogo de ITB.
 *
 * Cada pieza de la configuración tiene su lugar visible:
 *   brandColor            → héroe, chips, botones
 *   welcome               → la bajada del héroe
 *   bannerMessage+bannerBg→ el banner bajo el héroe
 *   announcement + phone  → la barra de anuncio con botón de WhatsApp
 *   showPackages          → el cierre que ofrece los paquetes de ITB
 *
 * El precio de cada tarjeta es `priceCents`, YA resuelto por el
 * servidor (el propio del franquiciatario o el de socio) — la vitrina
 * no calcula precios, los enseña.
 * ═══════════════════════════════════════════════════════════════════
 */
export function StorePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const store = usePublicStore(slug);
  const [category, setCategory] = useState<string | null>(null);

  const items = useMemo(() => store.data?.items ?? [], [store.data]);
  const available = useMemo(() => items.filter((item) => item.stock > 0), [items]);
  const categories = useMemo(
    () =>
      [...new Set(available.map((item) => item.product.category).filter(Boolean))].sort() as string[],
    [available],
  );
  const visible = useMemo(
    () => (category ? available.filter((item) => item.product.category === category) : available),
    [available, category],
  );

  if (store.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ds-cream">
        <p className="text-[14px] text-ds-ink-3">{t("Abriendo la tienda…")}</p>
      </div>
    );
  }

  if (store.isError || !store.data) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Alert variant="error" title={t("Esa tienda no existe")}>
          <Link to="/shop" className="underline underline-offset-2">
            {t("Ir a la tienda de It's The Best")}
          </Link>
        </Alert>
      </div>
    );
  }

  const info = store.data.store;
  const accent = info.branding.brandColor;
  const soft = (pct: number) => `color-mix(in srgb, ${accent} ${pct}%, transparent)`;
  const sf = info.branding;

  return (
    <StoreLayout store={info}>
      {/* ── La portada: el héroe de la casa, en SU tinta y con SUS
          productos flotando (StoreHero). ─────────────────────────────── */}
      <StoreHero store={info} items={items} accent={accent} />

      {/* Aire entre la portada y el contenido (Anahí, 27-ago). */}
      <div className="mx-auto w-full max-w-[1200px] space-y-6 px-4 pb-20 pt-10 sm:px-8 sm:pt-14">
        {/* ── El banner: su mensaje, su fondo ─────────────────────────── */}
        {sf.bannerMessage && (
          <div
            className="flex min-h-[88px] items-center rounded-3xl px-7 py-5 text-white shadow-ds-float"
            style={{ background: STORE_BANNER_BGS[sf.bannerBg] }}
          >
            <p className="text-lg font-semibold drop-shadow sm:text-xl">{sf.bannerMessage}</p>
          </div>
        )}

        {/* ── El anuncio y el contacto ────────────────────────────────── */}
        {(sf.announcement || sf.phone) && (
          <div
            className="flex flex-col justify-center gap-3 rounded-3xl border bg-white/70 px-6 py-5 backdrop-blur-sm"
            style={{ borderColor: soft(25) }}
          >
            {sf.announcement && (
              <div className="flex items-start gap-3">
                <span
                  className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl"
                  style={{ background: soft(12), color: accent }}
                >
                  <Megaphone className="h-4 w-4" aria-hidden />
                </span>
                <p className="text-[14px] leading-relaxed text-ds-ink">{sf.announcement}</p>
              </div>
            )}
            {sf.phone && (
              <a
                href={`https://wa.me/${sf.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white transition hover:opacity-90"
                style={{ background: accent }}
              >
                <Phone className="h-3.5 w-3.5" aria-hidden />
                {t("Escríbeme para más información")}
              </a>
            )}
          </div>
        )}

        {/* ── Chips de categoría, como la tienda de la casa ───────────── */}
        {available.length > 0 && categories.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            <CategoryChip
              label={t("Todos")}
              active={category === null}
              accent={accent}
              onClick={() => setCategory(null)}
            />
            {categories.map((cat) => (
              <CategoryChip
                key={cat}
                label={cat}
                active={category === cat}
                accent={accent}
                onClick={() => setCategory(cat === category ? null : cat)}
              />
            ))}
            <span className="ml-auto hidden text-[11px] uppercase tracking-ds-label text-ds-ink-3 sm:inline">
              {t("{{n}} productos", { n: visible.length })}
            </span>
          </div>
        )}

        {/* ── La rejilla ──────────────────────────────────────────────── */}
        {available.length === 0 ? (
          <div className="py-16">
            <EmptyState
              icon={<PackageOpen className="h-5 w-5 text-ds-ink-3" />}
              title={t("Esta tienda está reabasteciendo")}
              description={t("Vuelve pronto, o visita la tienda de It's The Best.")}
              action={
                <Button as={Link} to="/shop" size="sm" variant="secondary">
                  {t("Ir a It's The Best")}
                </Button>
              }
            />
          </div>
        ) : (
          /*
            La MISMA tarjeta del shop (Anahí, 27-ago): cápsula, foto 3:4,
            píldoras, precio apilado y "VER PRODUCTO" — aquí con su
            precio de tienda, su stock y el botón en su color.
          */
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((item) => (
              <ProductCard
                key={item.product.id}
                product={item.product}
                currency={STORE_CURRENCY}
                locale="es"
                to={`${storePath(info.slug)}/p/${item.product.slug}`}
                priceOverride={storeResolvedPrice(item)}
                unitsOverride={item.stock}
                accent={accent}
              />
            ))}
          </div>
        )}

        {/* ── El cierre de paquetes, si lo habilitó ───────────────────── */}
        {info.showPackages && (
          <section
            className="rounded-[28px] px-8 py-10 text-center text-white sm:px-12"
            style={{ background: accent }}
          >
            <p className="flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-ds-label text-white/70">
              <Boxes className="h-4 w-4" />
              {t("Emprende con {{name}}", { name: info.name })}
            </p>
            <h3 className="mt-2 text-2xl font-light sm:text-3xl">
              {t("¿Quieres ahorrar hasta un 15% en tus compras?")}
            </h3>
            <p className="mx-auto mt-2 max-w-lg text-[14px] text-white/80">
              {t(
                "Elige un paquete de It's The Best y quedas inscrito con el patrocinio de {{name}}.",
                { name: info.name },
              )}
            </p>
            <Link
              to="/paquetes"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink transition hover:opacity-90"
            >
              {t("Ver los paquetes")}
            </Link>
          </section>
        )}
      </div>
    </StoreLayout>
  );
}

function CategoryChip({
  label,
  active,
  accent,
  onClick,
}: {
  label: string;
  active: boolean;
  accent: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-full px-4 py-2 text-[12px] font-semibold tracking-wide text-white shadow-lg transition"
          : "rounded-full border border-ds-border bg-white px-4 py-2 text-[12px] font-semibold tracking-wide text-ds-ink-3 transition hover:text-ds-ink"
      }
      style={active ? { background: accent } : undefined}
    >
      {label}
    </button>
  );
}

/** El precio de la tienda como `ResolvedPrice` del shop: la cifra ya
 * resuelta por el servidor, y el público tachado si es más cara la casa. */
function storeResolvedPrice(item: StoreItem): ResolvedPrice | null {
  if (typeof item.priceCents !== "number") return null;
  const publicPrice = item.product.prices[STORE_CURRENCY];
  return {
    cents: item.priceCents,
    currency: STORE_CURRENCY,
    ...(typeof publicPrice === "number" && publicPrice > item.priceCents
      ? { compareAtCents: publicPrice }
      : {}),
    isFallbackCurrency: false,
    tier: "store",
  };
}
